import { LayoutGroup } from "motion/react"
import { memo, useCallback, useEffect, useRef } from "react"

import { NOISE_PNG } from "@/lib/noise-texture"
import type { AnimationEvent } from "@/types"

import { GridCell } from "./grid-cell"
import { PitchEventFlash } from "./pitch-event-flash"
import { PitchOverlayOver, PitchOverlayUnder } from "./pitch-overlays"
import type { PitchState } from "./use-pitch-state"

const COLS = 26
const ROWS = 15

/** Renders a golden ball indicator on the pitch (carried or loose). */
function BallMarker({ x, y }: { x: number; y: number }) {
  return (
    <div
      className="pointer-events-none absolute"
      style={{
        left: `calc(${(x + 0.5) / COLS} * 100%)`,
        top: `calc(${(y + 0.5) / ROWS} * 100%)`,
        transform: "translate(-50%, -50%)",
        zIndex: 15,
        transition: "left 0.3s ease-out, top 0.3s ease-out",
      }}
    >
      {/* Outer glow */}
      <div
        className="absolute rounded-full"
        style={{
          width: 32,
          height: 32,
          top: -16,
          left: -16,
          background:
            "radial-gradient(circle, oklch(0.78 0.18 55 / 0.45) 0%, transparent 70%)",
        }}
      />
      {/* Ball */}
      <div
        className="rounded-full border-2"
        style={{
          width: 14,
          height: 14,
          marginTop: -7,
          marginLeft: -7,
          background:
            "radial-gradient(circle at 35% 35%, oklch(0.85 0.15 55), oklch(0.60 0.15 45))",
          borderColor: "oklch(0.50 0.12 45)",
          boxShadow:
            "0 0 10px oklch(0.78 0.18 55 / 0.6), 0 1px 4px oklch(0 0 0 / 0.5)",
        }}
      />
    </div>
  )
}

/** Doubled miter-line frame for the pitch — broadcast monitor edge. */
function PitchNeonFrame() {
  return (
    <div className="pointer-events-none absolute -inset-3" aria-hidden="true">
      {/* ── Top edge ── */}
      {/* Outer (dim) */}
      <div
        className="absolute top-0 right-5 left-5 h-px"
        style={{ background: "oklch(0.8 0.3 145 / 0.5)" }}
      />
      {/* Inner (bright, breathing) */}
      <div
        className="animate-neon-breathe absolute top-1.5 right-3.5 left-3.5 h-px"
        style={{ background: "oklch(0.8 0.3 145)" }}
      />

      {/* ── Bottom edge ── */}
      <div
        className="absolute right-5 bottom-0 left-5 h-px"
        style={{ background: "oklch(0.8 0.3 145 / 0.5)" }}
      />
      <div
        className="absolute right-3.5 bottom-1.5 left-3.5 h-px"
        style={{ background: "oklch(0.8 0.3 145)" }}
      />

      {/* ── Left edge ── */}
      <div
        className="absolute top-5 bottom-5 left-0 w-px"
        style={{ background: "oklch(0.85 0.28 85 / 0.5)" }}
      />
      <div
        className="absolute top-3.5 bottom-3.5 left-1.5 w-px"
        style={{ background: "oklch(0.85 0.28 85)" }}
      />

      {/* ── Right edge ── */}
      <div
        className="absolute top-5 right-0 bottom-5 w-px"
        style={{ background: "oklch(0.85 0.28 85 / 0.5)" }}
      />
      <div
        className="absolute top-3.5 right-1.5 bottom-3.5 w-px"
        style={{ background: "oklch(0.85 0.28 85)" }}
      />
    </div>
  )
}

interface PitchGridProps {
  pitchState: PitchState
  homeTeamId: string
  homeTeamHue: number
  awayTeamHue: number
  /** The playerId of the player performing the current action (for highlight). */
  actingPlayerId?: string | null
  /** The oklch color string for the current action type highlight. */
  actionColor?: string | null
  /** The current animation event (for pitch overlays). */
  currentEvent?: AnimationEvent | null
  /** Player ID currently highlighted via narrative hover (for gold breathing glow). */
  highlightedPlayerId?: string | null
  /** Highlight type for big event overlays (touchdown/turnover). */
  highlightType?: "touchdown" | "turnover" | "kickoff" | null
  /** Key for AnimatePresence to re-trigger effects on consecutive same-type events. */
  eventKey?: number
  /** When provided, clicking a player token calls this with player ID and cell DOMRect. */
  onPlayerClick?: (playerId: string, rect: DOMRect) => void
  /** When true, suppresses NeonFrame and reduces padding (for mini replay contexts). */
  compact?: boolean
}

/**
 * Renders the 26×15 Blood Bowl pitch grid.
 *
 * Field markings (rendered as absolute overlays):
 * - Left end zone (col 0):  home team, team-hued gradient
 * - Right end zone (col 25): away team, team-hued gradient
 * - Wide zone lines at cols 3 and 23 (dashed, dim neon-gold)
 * - Center (scrimmage) line at 50% width (dim neon-green with glow)
 *
 * Player tokens are rendered inside the corresponding grid cells.
 * Wraps cells with LayoutGroup to enable motion.dev layoutId cross-cell
 * animations when players move between squares.
 */
export const PitchGrid = memo(function PitchGrid({
  pitchState,
  homeTeamId,
  homeTeamHue,
  awayTeamHue,
  actingPlayerId,
  actionColor,
  currentEvent,
  highlightedPlayerId,
  highlightType,
  eventKey = 0,
  onPlayerClick,
  compact = false,
}: PitchGridProps) {
  // ─── Turnover screen shake ───────────────────────────────────────────
  const pitchRef = useRef<HTMLDivElement>(null)
  const shakeRef = useRef<number | undefined>(undefined)

  const triggerShake = useCallback(() => {
    const el = pitchRef.current
    if (!el) return
    // Remove + re-add class to re-trigger for consecutive turnovers
    el.classList.remove("pitch-shake")
    // Force reflow so the browser recognises the removal
    void el.offsetWidth
    el.classList.add("pitch-shake")
    clearTimeout(shakeRef.current)
    shakeRef.current = window.setTimeout(() => {
      el.classList.remove("pitch-shake")
    }, 300)
  }, [])

  useEffect(() => {
    if (highlightType === "turnover") {
      triggerShake()
    }
  }, [highlightType, eventKey, triggerShake])
  // Find the grid position of the acting player (for highlight)
  let actingPlayerKey: string | null = null
  if (actingPlayerId) {
    for (const [key, player] of pitchState.playerPositions) {
      if (player.playerId === actingPlayerId) {
        actingPlayerKey = key
        break
      }
    }
  }

  const cells = []
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      const key = `${col},${row}`
      const isActingCell = actingPlayerKey === key
      cells.push(
        <GridCell
          key={key}
          x={col}
          y={row}
          player={pitchState.playerPositions.get(key)}
          homeTeamId={homeTeamId}
          homeTeamHue={homeTeamHue}
          awayTeamHue={awayTeamHue}
          highlightColor={isActingCell ? actionColor : null}
          actingPlayerId={actingPlayerId}
          highlightedPlayerId={highlightedPlayerId}
          onPlayerClick={onPlayerClick}
        />,
      )
    }
  }

  return (
    <div
      className={`relative flex h-full items-center justify-center overflow-hidden ${compact ? "p-0" : "p-5"}`}
    >
      <div
        ref={pitchRef}
        className="relative w-full max-w-[1100px]"
        style={{ aspectRatio: "26 / 15" }}
        data-testid="pitch-grid"
      >
        {/* Doubled miter-line frame — broadcast monitor edge */}
        {!compact && <PitchNeonFrame />}

        {/* Pitch surface */}
        <div
          className={`absolute inset-0 ${compact ? "" : "border-[2px]"}`}
          style={{
            background:
              "radial-gradient(ellipse at 50% 45%, oklch(0.30 0.10 145), oklch(0.24 0.08 145))",
            borderColor: compact ? undefined : "oklch(0.15 0.01 240)",
            boxShadow: compact
              ? "inset 0 0 40px oklch(0 0 0 / 0.3)"
              : "inset 0 0 40px oklch(0 0 0 / 0.3), 0 4px 20px oklch(0 0 0 / 0.5)",
          }}
        />

        {/* Checkerboard — aligned to the 26×15 grid */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "conic-gradient(oklch(0 0 0 / 0.08) 0.25turn, transparent 0.25turn 0.5turn, oklch(0 0 0 / 0.08) 0.5turn 0.75turn, transparent 0.75turn)",
            backgroundSize: `${100 / 13}% ${100 / 7.5}%`,
            zIndex: 1,
          }}
        />

        {/* Noise grain overlay — ink wash paper feel */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: `url('${NOISE_PNG}')`,
            backgroundRepeat: "repeat",
            opacity: 0.06,
            mixBlendMode: "overlay" as const,
            zIndex: 1,
          }}
        />

        {/* Cinematic vignette */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            boxShadow: "inset 0 0 60px 15px oklch(0 0 0 / 0.35)",
            zIndex: 1,
          }}
        />

        {/* Field markings overlays */}

        {/* Left end zone (col 0) — home */}
        <div
          className="absolute top-0 bottom-0 left-0"
          style={{
            width: `calc(100% / ${COLS})`,
            background: `linear-gradient(90deg, oklch(0.18 0.12 ${homeTeamHue} / 0.55), oklch(0.20 0.08 ${homeTeamHue} / 0.25))`,
            backgroundImage: `repeating-linear-gradient(-45deg, oklch(0.30 0.15 ${homeTeamHue} / 0.15) 0px, oklch(0.30 0.15 ${homeTeamHue} / 0.15) 3px, transparent 3px, transparent 8px), linear-gradient(90deg, oklch(0.18 0.12 ${homeTeamHue} / 0.55), oklch(0.20 0.08 ${homeTeamHue} / 0.25))`,
            borderRight: `3px solid oklch(0.50 0.15 ${homeTeamHue} / 0.6)`,
          }}
        />

        {/* Right end zone (col 25) — away */}
        <div
          className="absolute top-0 right-0 bottom-0"
          style={{
            width: `calc(100% / ${COLS})`,
            background: `linear-gradient(270deg, oklch(0.18 0.12 ${awayTeamHue} / 0.55), oklch(0.20 0.08 ${awayTeamHue} / 0.25))`,
            backgroundImage: `repeating-linear-gradient(-45deg, oklch(0.30 0.15 ${awayTeamHue} / 0.15) 0px, oklch(0.30 0.15 ${awayTeamHue} / 0.15) 3px, transparent 3px, transparent 8px), linear-gradient(270deg, oklch(0.18 0.12 ${awayTeamHue} / 0.55), oklch(0.20 0.08 ${awayTeamHue} / 0.25))`,
            borderLeft: `3px solid oklch(0.50 0.15 ${awayTeamHue} / 0.6)`,
          }}
        />

        {/* Left wide zone boundary (after col 2, i.e. at 3/26) */}
        <div
          className="absolute top-0 bottom-0"
          style={{
            left: `calc(100% * 3 / ${COLS})`,
            width: "1px",
            borderLeft: "1px dashed oklch(0.65 0.20 85 / 0.25)",
          }}
        />

        {/* Right wide zone boundary (before col 23, i.e. at 23/26) */}
        <div
          className="absolute top-0 bottom-0"
          style={{
            left: `calc(100% * 23 / ${COLS})`,
            width: "1px",
            borderLeft: "1px dashed oklch(0.65 0.20 85 / 0.25)",
          }}
        />

        {/* Center / scrimmage line */}
        <div
          className="absolute top-0 bottom-0"
          style={{
            left: "50%",
            width: "3px",
            background: "oklch(0.60 0.22 145 / 0.6)",
            transform: "translateX(-50%)",
            boxShadow: "0 0 8px oklch(0.60 0.22 145 / 0.15)",
          }}
        />

        {/* Push arrows (under player tokens) */}
        <PitchOverlayUnder
          event={currentEvent ?? null}
          actionColor={actionColor ?? null}
          pitchState={pitchState}
        />

        {/* CSS Grid for cells — wrapped in LayoutGroup for motion.dev layoutId animations */}
        <LayoutGroup>
          <div
            className="absolute inset-0"
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${COLS}, 1fr)`,
              gridTemplateRows: `repeat(${ROWS}, 1fr)`,
            }}
          >
            {cells}
          </div>
        </LayoutGroup>

        {/* Ball indicator — always rendered at ballPosition */}
        {pitchState.ballPosition && (
          <BallMarker
            x={pitchState.ballPosition[0]}
            y={pitchState.ballPosition[1]}
          />
        )}

        {/* Movement paths, block arrows, blitz overlays (over player tokens) */}
        <PitchOverlayOver
          event={currentEvent ?? null}
          actionColor={actionColor ?? null}
          pitchState={pitchState}
        />

        {/* Big event flash (touchdown / turnover) — topmost overlay */}
        <PitchEventFlash
          highlightType={highlightType ?? null}
          eventKey={eventKey}
        />
      </div>
    </div>
  )
})
